// Tonk on screen (desktop + phone): the join prompt, the lobby (with the
// hands-per-game option), and the game — opponents' card counts, the stock
// and discard, spreads on the table, your hand (tap to select) and the
// Drop / Spread / Hit / Discard buttons. VR uses TonkVRPanel.

import React from 'react';
import { tableAction, handOf, turnDeadline } from '../gameStore';
import { cardLabel, cardName, isRed } from '../cardFaces';
import { handTotal, HAND_OPTIONS, DEFAULT_HANDS, type TonkState, type TonkMove } from '../../../../supabase/functions/games/tonk.ts';
import { JoinPrompt, LobbyPanel, HelpButton, GameOverBlock, PanelError, TurnTimer, leaveTable } from '../kit/LobbyKit';
import { tonkName, isTurn, hitTargets, canSpread, handLine, useSelection } from './tonkView';
import type { OverlayProps } from '../types';
import './TonkOverlay.css';

function Mini({ card, hl = false }: { card: string; hl?: boolean }) {
  return <span className={`gx-tk-mini ${isRed(card) ? 'is-red' : ''} ${hl ? 'is-hl' : ''}`} aria-label={cardName(card)}>{cardLabel(card)}</span>;
}

export function TonkOverlay(props: OverlayProps) {
  const { def, pid, entry, table, seat, onHelp } = props;
  const st = table.state;
  const g = (st.g ?? null) as TonkState | null;
  const hand = handOf(entry);
  const { sel, setSel, toggle } = useSelection(hand, g ? `${g.handNo}:${g.turn}:${g.phase}` : '');

  if (seat < 0) return <JoinPrompt {...props} />;

  if (st.phase === 'lobby') {
    const cur = Number(st.options?.hands) || DEFAULT_HANDS;
    return (
      <LobbyPanel {...props}
        footer={<span className="gx-tk-opts" role="group" aria-label="Hands per game">
          Hands:{' '}
          {HAND_OPTIONS.map((n) => (
            <button key={n} className={`gx-sp-btn gx-sp-btn--ghost gx-tk-opt ${n === cur ? 'is-on' : ''}`} aria-pressed={n === cur}
              disabled={entry.busy} onClick={() => void tableAction(pid, 'configure', { options: { hands: n } })}>{n}</button>
          ))}
          <span className="gx-tk-optnote">2–4 players. Highest score after {cur} hands wins.</span>
        </span>} />
    );
  }
  if (!g) return null;

  const send = (move: TonkMove) => { setSel([]); void tableAction(pid, 'move', { move }); };
  const myTurn = isTurn(st, g, seat);
  const drawPhase = myTurn && g.phase === 'draw';
  const playPhase = myTurn && g.phase === 'play';
  const one = sel.length === 1 ? sel[0] : undefined;
  const targets = playPhase ? hitTargets(g, one) : [];
  const top = g.discard[g.discard.length - 1];
  const busy = entry.busy;
  const total = handTotal(hand);
  const others = g.active.filter((i) => i !== seat);
  const handOver = g.phase === 'hand_over' || g.phase === 'game_over';

  let status: string;
  if (st.phase === 'over') {
    status = g.winner === seat ? 'You win the game!' : g.winner === null ? 'Drawn game' : `${tonkName(st, g.winner)} wins the game`;
  } else if (g.phase === 'hand_over') status = `${handLine(st, g, seat)} — next deal soon`;
  else if (drawPhase) status = 'Your turn — draw a card, or drop';
  else if (playPhase) status = 'Spread / hit, then discard to end your turn';
  else status = `${tonkName(st, g.turn)}'s turn`;

  const discardBlocked = one !== undefined && one === g.takenFromDiscard && hand.length > 1;

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game gx-tk ${myTurn ? 'is-my-turn' : ''}`} role="region" aria-label="Tonk game">
      <div className="gx-sp-head">
        <span className="gx-sp-prompt-title"><span className="gx-sp-spade">{def.glyph}</span> Hand {g.handNo}/{g.handsToPlay}{g.handNo > g.handsToPlay ? ' (extra)' : ''}</span>
        <div className="gx-sp-head-actions">
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>

      <div className="gx-tk-players">
        {[seat, ...others].map((i) => (
          <div key={i} className={`gx-tk-player ${isTurn(st, g, i) ? 'is-turn' : ''} ${i === seat ? 'is-me' : ''}`}>
            <span className="gx-tk-pname">{i === seat ? 'You' : tonkName(st, i)}</span>
            <span className="gx-tk-pscore"><b>{g.scores[i]}</b> pts</span>
            {!handOver && <span className="gx-tk-backs" aria-label={`${g.counts[i]} cards`}>
              {Array.from({ length: Math.min(g.counts[i], 8) }, (_, k) => <span key={k} className="gx-tk-back" />)}
              <small>{g.counts[i]}</small>
            </span>}
            {handOver && g.lastHand?.hands[i] && (
              <span className="gx-tk-shown">
                {g.lastHand.hands[i]!.map((c) => <Mini key={c} card={c} />)}
                <small>= {g.lastHand.totals[i]}{g.lastHand.points[i] ? ` · +${g.lastHand.points[i]}` : ''}</small>
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="gx-tk-felt">
        <div className="gx-tk-piles">
          <button className="gx-tk-pile gx-tk-stock" disabled={!drawPhase || busy || g.stockCount === 0}
            onClick={() => send({ type: 'draw', from: 'stock' })} aria-label={`Draw from the stock (${g.stockCount} left)`}>
            <span className="gx-tk-back gx-tk-back--big" />
            <small>Stock {g.stockCount}</small>
          </button>
          <button className="gx-tk-pile" disabled={!drawPhase || busy || !top}
            onClick={() => send({ type: 'draw', from: 'discard' })} aria-label={top ? `Take the ${cardName(top)}` : 'Discard pile empty'}>
            {top ? <Mini card={top} /> : <span className="gx-tk-mini is-empty" />}
            <small>Discard</small>
          </button>
          <div className="gx-tk-status">
            <span className={`gx-tk-line ${myTurn ? 'is-mine' : ''}`}>{status}</span>
            {myTurn && <TurnTimer deadline={turnDeadline(st)} />}
          </div>
        </div>
        {g.spreads.length > 0 && (
          <div className="gx-tk-spreads" role="group" aria-label="Spreads on the table">
            {g.spreads.map((sp) => {
              const fits = targets.some((t) => t.id === sp.id);
              return (
                <button key={sp.id} className={`gx-tk-spread ${fits ? 'is-fit' : ''}`} disabled={!fits || busy}
                  aria-label={`${tonkName(st, sp.owner)}'s spread${fits ? ` — hit with ${cardName(one!)}` : ''}`}
                  onClick={() => one && send({ type: 'hit', card: one, spread: sp.id })}>
                  {sp.cards.map((c) => <Mini key={c} card={c} />)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {st.phase === 'over' ? (
        <GameOverBlock {...props} summary={`${status} · ${g.active.map((i) => `${i === seat ? 'You' : tonkName(st, i)} ${g.scores[i]}`).join(' · ')}`} />
      ) : hand.length > 0 && !handOver ? (
        <>
          <div className="gx-tk-actions">
            {drawPhase && (
              <button className="gx-sp-btn gx-tk-drop" disabled={busy} onClick={() => send({ type: 'drop' })}>Drop ({total})</button>
            )}
            {playPhase && <>
              <button className="gx-sp-btn" disabled={busy || !canSpread(sel)} onClick={() => send({ type: 'spread', cards: sel })}>Spread</button>
              <button className="gx-sp-btn" disabled={busy || !targets.length} onClick={() => one && send({ type: 'hit', card: one, spread: targets[0].id })}>Hit</button>
              <button className="gx-sp-btn gx-sp-btn--primary" disabled={busy || !one || discardBlocked} onClick={() => one && send({ type: 'discard', card: one })}>Discard</button>
            </>}
            <span className="gx-sp-note gx-tk-total">Your total <b>{total}</b></span>
          </div>
          {playPhase && <span className="gx-sp-note gx-tk-hint">
            {discardBlocked ? "You can't throw back the card you just took"
              : sel.length === 0 ? 'Tap cards to select them'
              : one ? (targets.length > 1 ? 'Tap a highlighted spread to hit it, or discard' : 'Discard it, or hit a spread')
              : canSpread(sel) ? 'Ready to spread' : 'Not a spread yet: 3+ of a kind or a suited run'}
          </span>}
          <div className="gx-tk-hand" role="group" aria-label="Your hand">
            {hand.map((c) => {
              const on = sel.includes(c);
              return (
                <span key={c} className="gx-tk-slot">
                  <button className={`gx-tk-card ${isRed(c) ? 'is-red' : ''} ${playPhase ? 'is-playable' : ''} ${on ? 'is-sel' : ''}`}
                    disabled={!playPhase || busy} aria-pressed={on} aria-label={cardName(c)} onClick={() => toggle(c)}>
                    <span className="gx-tk-card-rank">{cardLabel(c).slice(0, -1)}</span>
                    <span className="gx-tk-card-suit">{cardLabel(c).slice(-1)}</span>
                  </button>
                </span>
              );
            })}
          </div>
        </>
      ) : null}
      {st.note && <span className="gx-sp-note gx-sp-log">{st.note}</span>}
      <PanelError entry={entry} />
    </div>
  );
}

export default TonkOverlay;
